import { useSlots, computed, useCssModule, watch, onMounted, ref, nextTick } from "vue";
import { u as useI18n } from "./index-CgZzOVq6.js";
import { isVue2 } from "vue-demi";
import { u as useDataTestIdProp } from "./index-BNd8MWnO.js";
import { u as useFullWidthProp } from "./index-4vIud5LZ.js";
import { X as XIcon, u as useIconProps } from "./XIcon-U9bwokTN.js";
import "./index-vzP2cezV.js";
import "vue-router";
import "dompurify";
import { useElementHover } from "@vueuse/core";
import XLink from "../XLink.js";
import { n as normalizeComponent } from "./_plugin-vue2_normalizer-VvYS6rEj.js";
function useCheckEmptySlot(slotName = "default") {
  const slots = useSlots();
  const hasContent = computed(() => {
    var _a, _b, _c;
    if (!slots[slotName] || ((_a = slots[slotName]()) == null ? void 0 : _a.length) === 0) return false;
    const slotContent = slots[slotName]()[0];
    if (isVue2) {
      return !!((_b = String(slotContent.text)) == null ? void 0 : _b.replace(/\s+/g, ""));
    }
    return !!((_c = String(slotContent.children)) == null ? void 0 : _c.replace(/\s+/g, ""));
  });
  return {
    hasContent
  };
}
function usePositions() {
  const POSITIONS = {
    topRight: "top-right",
    bottomLeft: "bottom-left",
    bottomRight: "bottom-right",
    topLeft: "top-left"
  };
  function createPositionProp2() {
    return {
      position: {
        type: String,
        default: POSITIONS.topRight
      }
    };
  }
  function getCurrentPositionClass2(props) {
    const $style = useCssModule();
    const positionClass = computed(() => ({
      [$style.isTopRight]: props.position === POSITIONS.topRight,
      [$style.isTopLeft]: props.position === POSITIONS.topLeft,
      [$style.isBottomRight]: props.position === POSITIONS.bottomRight,
      [$style.isBottomLeft]: props.position === POSITIONS.bottomLeft
    }));
    return {
      positionClass
    };
  }
  return {
    createPositionProp: createPositionProp2,
    getCurrentPositionClass: getCurrentPositionClass2,
    POSITIONS
  };
}
const MODELS = {
  inline: {
    name: "inline",
    className: "modelInline"
  },
  banner: {
    name: "banner",
    className: "modelBanner"
  },
  toast: {
    name: "toast",
    className: "modelToast"
  }
};
const MODELS_NAMES = Object.values(MODELS).map(({ name }) => name);
function useModels(props) {
  const isInlineModel = computed(() => props.model === MODELS.inline.name);
  const isBannerModel = computed(() => props.model === MODELS.banner.name);
  const isToastModel = computed(() => props.model === MODELS.toast.name);
  return {
    isInlineModel,
    isBannerModel,
    isToastModel
  };
}
const { getFullWidthClass } = useFullWidthProp();
const { getCurrentPositionClass } = usePositions();
function useClasses(props) {
  const $style = useCssModule();
  const { fullWidthClass } = getFullWidthClass(props);
  const { positionClass } = getCurrentPositionClass(props);
  const { isInlineModel, isBannerModel, isToastModel } = useModels(props);
  const modelClass = computed(() => ({
    [$style[MODELS.inline.className]]: isInlineModel.value,
    [$style[MODELS.banner.className]]: isBannerModel.value,
    [$style[MODELS.toast.className]]: isToastModel.value
  }));
  const alertClasses = computed(() => ({
    [$style[props.type]]: !!props.type,
    [$style.hasElevation]: props.elevation,
    [$style.noTitle]: !props.title,
    [$style.isClosable]: props.closable,
    ...modelClass.value,
    ...fullWidthClass.value,
    ...positionClass.value
  }));
  return { alertClasses, positionClass };
}
const DURATION = 7e3;
const DURATION_EXTRA_BUFFER = 1e3;
function useCloseTimer(options) {
  const { alertRef, timeBarRef, props, callback } = options;
  let isClosed = false;
  let closeTimer = null;
  let closeTimerStartTime;
  let remainingDurationToClose = props.duration;
  const isHovered = useElementHover(alertRef);
  const { isToastModel } = useModels(props);
  function startCloseTimer() {
    if (isToastModel.value && !isClosed) {
      closeTimerStartTime = Date.now();
      setTimeBarState();
      closeTimer = setTimeout(() => {
        callback();
      }, remainingDurationToClose);
    }
  }
  function pauseCloseTimer() {
    if (isToastModel.value) {
      clearCloseTimer();
      const closeTimerElapsedTime = Date.now() - closeTimerStartTime;
      setTimeBarState("paused");
      remainingDurationToClose -= closeTimerElapsedTime;
    }
  }
  function finishCloseTimer() {
    if (isToastModel.value) {
      clearCloseTimer();
      isClosed = true;
    }
  }
  function clearCloseTimer() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }
  function setTimeBarState(state = "running") {
    var _a;
    if ((_a = timeBarRef.value) == null ? void 0 : _a.style) {
      timeBarRef.value.style.animationPlayState = state;
    }
  }
  function setTimeBarDuration() {
    if (timeBarRef.value && isToastModel.value) {
      timeBarRef.value.style.animationDuration = `${props.duration}ms`;
    }
  }
  watch(isHovered, (hovered) => {
    if (hovered) {
      pauseCloseTimer();
    } else {
      startCloseTimer();
    }
  });
  watch(
    () => props.isVisible,
    (newVisibility) => {
      if (newVisibility) {
        startCloseTimer();
      }
    },
    { immediate: true }
  );
  onMounted(() => {
    setTimeBarDuration();
  });
  return {
    finishCloseTimer
  };
}
const TYPES = {
  info: {
    name: "info",
    icon: "circle-info"
  },
  success: {
    name: "success",
    icon: "circle-check"
  },
  warning: {
    name: "warning",
    icon: "triangle-exclamation"
  },
  error: {
    name: "error",
    icon: "circle-xmark"
  }
};
const TYPES_NAMES = Object.values(TYPES).map(({ name }) => name);
function useTypes(props) {
  const activeIcon = computed(() => props.icon || TYPES[props.type].icon);
  return {
    activeIcon
  };
}
const fadeEnterActive = "_fadeEnterActive_1q30g_54";
const fadeIn = "_fadeIn_1q30g_1";
const fadeLeaveActive = "_fadeLeaveActive_1q30g_58";
const fadeOut = "_fadeOut_1q30g_1";
const xAlert = "_xAlert_1q30g_62";
const modelInline = "_modelInline_1q30g_68";
const info = "_info_1q30g_77";
const success = "_success_1q30g_82";
const error = "_error_1q30g_87";
const warning = "_warning_1q30g_92";
const modelBanner = "_modelBanner_1q30g_97";
const details = "_details_1q30g_123";
const title = "_title_1q30g_129";
const actions = "_actions_1q30g_132";
const isClosable = "_isClosable_1q30g_137";
const modelToast = "_modelToast_1q30g_141";
const isTopRight = "_isTopRight_1q30g_148";
const slideEnterActive = "_slideEnterActive_1q30g_148";
const isBottomRight = "_isBottomRight_1q30g_148";
const slideEnterRight = "_slideEnterRight_1q30g_1";
const slideLeaveActive = "_slideLeaveActive_1q30g_154";
const slideLeaveRight = "_slideLeaveRight_1q30g_1";
const isTopLeft = "_isTopLeft_1q30g_160";
const isBottomLeft = "_isBottomLeft_1q30g_160";
const slideEnterLeft = "_slideEnterLeft_1q30g_1";
const slideLeaveLeft = "_slideLeaveLeft_1q30g_1";
const timeBar = "_timeBar_1q30g_188";
const timeout = "_timeout_1q30g_1";
const isFullWidth = "_isFullWidth_1q30g_218";
const container = "_container_1q30g_221";
const icon = "_icon_1q30g_230";
const text = "_text_1q30g_251";
const link = "_link_1q30g_267";
const closeButton = "_closeButton_1q30g_275";
const closeIcon = "_closeIcon_1q30g_288";
const isDisabled = "_isDisabled_1q30g_291";
const style0 = {
  fadeEnterActive,
  fadeIn,
  fadeLeaveActive,
  fadeOut,
  xAlert,
  modelInline,
  info,
  success,
  error,
  warning,
  modelBanner,
  details,
  title,
  actions,
  isClosable,
  modelToast,
  isTopRight,
  slideEnterActive,
  isBottomRight,
  slideEnterRight,
  slideLeaveActive,
  slideLeaveRight,
  isTopLeft,
  isBottomLeft,
  slideEnterLeft,
  slideLeaveLeft,
  timeBar,
  timeout,
  isFullWidth,
  container,
  icon,
  text,
  link,
  closeButton,
  closeIcon,
  isDisabled
};
const { createIconProps } = useIconProps();
const { createTestIdProp, setupTestId } = useDataTestIdProp();
const { createFullWidthProp } = useFullWidthProp();
const { createPositionProp } = usePositions();
const __default__ = {
  name: "XAlert",
  components: { XIcon, XLink }
};
const _sfc_main = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    type: {
      type: String,
      default: TYPES.info.name,
      validator: (status) => TYPES_NAMES.includes(status)
    },
    model: {
      type: String,
      default: MODELS.inline.name,
      validator: (status) => MODELS_NAMES.includes(status)
    },
    title: {
      type: String,
      default: ""
    },
    actions: {
      type: Array,
      default: () => []
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    closable: {
      type: Boolean,
      default: false
    },
    duration: {
      type: Number,
      default: DURATION
    },
    ...createPositionProp(),
    ...createFullWidthProp(),
    ...createIconProps(),
    ...createTestIdProp()
  },
  emits: ["close"],
  setup(__props, { emit }) {
    const props = __props;
    const { t } = useI18n();
    const $style = useCssModule();
    const { activeIcon } = useTypes(props);
    const { alertClasses } = useClasses(props);
    const { formatTestId } = setupTestId(props);
    const { isToastModel } = useModels(props);
    const { hasContent } = useCheckEmptySlot();
    const timeBarRef = ref(null);
    const alertRef = ref(null);
    const canShowAlert = ref(false);
    const { finishCloseTimer } = useCloseTimer({
      alertRef,
      timeBarRef,
      props,
      callback: () => close()
    });
    const animationName = computed(() => isToastModel.value ? "slide" : "fade");
    const animationClasses = computed(() => ({
      enter: $style[`${animationName.value}EnterActive`],
      leave: $style[`${animationName.value}LeaveActive`]
    }));
    const testIds = computed(() => ({
      base: formatTestId(),
      icon: formatTestId("icon"),
      title: formatTestId("title"),
      link: formatTestId("link"),
      text: formatTestId("text"),
      timeBar: formatTestId("timeBar"),
      closeButton: formatTestId("closeButton"),
      closeButtonIcon: formatTestId("closeButtonIcon")
    }));
    function setCanShowAlert(status) {
      canShowAlert.value = status;
    }
    function close() {
      finishCloseTimer();
      emit("close", false);
      setCanShowAlert(false);
    }
    watch(
      () => props.isVisible,
      async (newVisibility) => {
        if (isToastModel.value) {
          await nextTick(() => {
            setCanShowAlert(newVisibility);
          });
        } else {
          setCanShowAlert(newVisibility);
        }
      },
      { immediate: true }
    );
    return { __sfc: true, createIconProps, createTestIdProp, setupTestId, createFullWidthProp, createPositionProp, props, emit, t, $style, activeIcon, alertClasses, formatTestId, isToastModel, hasContent, timeBarRef, alertRef, canShowAlert, finishCloseTimer, animationName, animationClasses, testIds, setCanShowAlert, close, XIcon, XLink };
  }
});
var _sfc_render = function render() {
  var _vm = this, _c = _vm._self._c, _setup = _vm._self._setupProxy;
  return _c("transition", { attrs: { "name": _setup.animationName, "enter-active-class": _setup.animationClasses.enter, "leave-active-class": _setup.animationClasses.leave } }, [_c("div", { directives: [{ name: "show", rawName: "v-show", value: _setup.canShowAlert, expression: "canShowAlert" }], ref: "alertRef", class: [_setup.$style.xAlert, _setup.alertClasses], attrs: { "data-testid": _setup.testIds.base } }, [_c("div", { class: _setup.$style.container }, [_setup.activeIcon ? _c(_setup.XIcon, { class: _setup.$style.icon, attrs: { "icon": _setup.activeIcon, "icon-style": _vm.iconStyle, "data-testid": _setup.testIds.icon } }) : _vm._e(), _c("div", { class: _setup.$style.details, attrs: { "role": "alert", "aria-live": "polite" } }, [_vm.title ? _c("h5", { class: _setup.$style.title, attrs: { "data-testid": _setup.testIds.title } }, [_vm._v(" " + _vm._s(_vm.title) + " ")]) : _vm._e(), _setup.hasContent ? _c("div", { class: _setup.$style.text, attrs: { "data-testid": _setup.testIds.text } }, [_vm._t("default")], 2) : _vm._e(), _vm.actions.length > 0 ? _c("div", { class: _setup.$style.actions }, _vm._l(_vm.actions, function(action) {
    return _c(_setup.XLink, { key: `action-${action.text}`, class: _setup.$style.link, attrs: { "to": action.to, "external": action == null ? void 0 : action.external, "native": action == null ? void 0 : action.native, "target": action == null ? void 0 : action.target, "data-testid": (action == null ? void 0 : action.dataTestId) || _setup.testIds.link, "no-padding": "" } }, [_vm._v(_vm._s(action.text))]);
  }), 1) : _vm._e()]), _vm.closable ? _c("button", { class: _setup.$style.closeButton, attrs: { "data-testid": _setup.testIds.closeButton, "title": _setup.t("ds.xAlert.actions.close") }, on: { "click": function($event) {
    return _setup.close();
  } } }, [_c(_setup.XIcon, { class: _setup.$style.closeIcon, attrs: { "icon": "xmark-large", "size": "sm", "icon-style": "fas", "data-testid": _setup.testIds.closeButtonIcon } })], 1) : _vm._e()], 1), _c("div", { directives: [{ name: "show", rawName: "v-show", value: _setup.isToastModel, expression: "isToastModel" }], ref: "timeBarRef", class: _setup.$style.timeBar, attrs: { "data-testid": _setup.testIds.timeBar } })])]);
};
var _sfc_staticRenderFns = [];
const __cssModules = {
  "$style": style0
};
function _sfc_injectStyles(ctx) {
  for (var key in __cssModules) {
    this[key] = __cssModules[key];
  }
}
var __component__ = /* @__PURE__ */ normalizeComponent(
  _sfc_main,
  _sfc_render,
  _sfc_staticRenderFns,
  false,
  _sfc_injectStyles
);
const XAlert = __component__.exports;
export {
  DURATION_EXTRA_BUFFER as D,
  XAlert as X,
  usePositions as u
};
